import { ErrorView } from "@/components/ErrorView";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { coverageText } from "@/lib/reply-coverage";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AtSign, Ban, MessageCircleOff, UserPlus, type LucideIcon } from "lucide-react";
import { useId, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { rowMode } from "./channel-mode";
import { peopleKey, rolesKey } from "./people/use-identity";

export const EDIT_REPLY_LABEL = "Edit not-available reply";

// The "When Boo answers" block, the same at the top of Channels and both
// People screens: who Boo answers, and its not-available reply. Pass
// replyOpen / onReplyOpenChange to open the reply dialog from elsewhere on
// the page.
export function WhenBooAnswers({
  containerId,
  replyOpen,
  onReplyOpenChange,
}: {
  containerId: string;
  replyOpen?: boolean;
  onReplyOpenChange?: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [ownReplyOpen, setOwnReplyOpen] = useState(false);
  const dialogOpen = replyOpen ?? ownReplyOpen;
  const setDialogOpen = onReplyOpenChange ?? setOwnReplyOpen;
  const query = useQuery({
    queryKey: ["channels", containerId],
    queryFn: () => api.containers.channels.list(containerId),
    enabled: !!containerId,
  });
  // Count people the way People's Blocked filter does. Older Boos have no
  // people list, only chat accounts, and count those. The roles query (which
  // tells the two apart) is only read here, never fetched: the Boo's layout
  // fetches it, and a second fetching observer would refetch it on every mount.
  const roles = useQuery({
    queryKey: rolesKey(containerId),
    queryFn: () => api.containers.roles.get(containerId),
    enabled: false,
  });
  const isV2 = !!roles.data;
  const people = useQuery({
    queryKey: peopleKey(containerId),
    queryFn: () => api.containers.people.list(containerId),
    enabled: !!containerId && isV2,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["channels", containerId] });
    queryClient.invalidateQueries({ queryKey: peopleKey(containerId) });
    queryClient.invalidateQueries({ queryKey: ["boo-access-events", containerId] });
  };
  const onError = (err: unknown) => toast.error(err instanceof Error ? err.message : "That didn't save. Try again.");
  const setBlockNewUsers = useMutation({
    mutationFn: (next: boolean) => api.containers.channels.setBlockNewUsers(containerId, next),
    onSuccess: refresh,
    onError,
  });
  const setRejection = useMutation({
    mutationFn: (message: string | null) => api.containers.channels.setRejectionMessage(containerId, message),
    onSuccess: refresh,
    onError,
  });

  const heading = (
    <h2 id="when-boo-answers" className="text-base font-semibold">
      When Boo answers
    </h2>
  );
  // A people list that fails (or that this viewer can't read) falls back to
  // counting chat accounts rather than holding the block back.
  const peopleCounted = isV2 ? !!people.data || people.isError : roles.fetchStatus !== "fetching";
  if (query.isError) {
    return (
      <section className="flex flex-col gap-3" aria-labelledby="when-boo-answers">
        {heading}
        <ErrorView
          variant="inline"
          error={query.error ?? "Couldn't load who Boo answers."}
          onRetry={() => void query.refetch()}
        />
      </section>
    );
  }
  if (!query.data || !peopleCounted) return <Skeleton className="h-44 w-full rounded-lg" />;

  const data = query.data;
  const canToggle = data._permissions?.canToggle !== false;
  // A Boo from before per-channel modes reports neither setting.
  const hasNewPeople = data.blockNewUsers !== undefined;
  const blockNewUsers = data.blockNewUsers ?? false;
  const rejectionMessage = data.rejectionMessage ?? null;
  const hasReply = !!data.surfaceDefaults;
  if (!hasNewPeople && !hasReply) return null;
  const quietCount = data.channels.filter((ch) => rowMode(ch) === "never").length;
  const blockedCount = people.data
    ? people.data.data.filter((p) => p.blocked).length
    : (data.users ?? []).filter((u) => u.blocked).length;

  return (
    <section className="flex flex-col gap-3" aria-labelledby="when-boo-answers">
      {heading}
      {hasNewPeople && (
        <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
          <div className="min-w-0">
            <span className="text-sm font-medium">New people start as Blocked</span>
            <p className="text-sm text-muted-foreground text-pretty">
              {blockNewUsers
                ? "Someone new gets Boo's not-available reply until an admin changes them from Blocked to Standard. People already listed aren't affected."
                : "Boo answers anyone in your workspace you haven't blocked."}
            </p>
          </div>
          <Switch
            checked={blockNewUsers}
            onCheckedChange={(v) => setBlockNewUsers.mutate(v)}
            disabled={!canToggle || setBlockNewUsers.isPending}
            aria-label="New people start as Blocked"
          />
        </div>
      )}
      {hasReply && (
        <ReplyBar
          rejectionMessage={rejectionMessage}
          quietCount={quietCount}
          blockedCount={blockedCount}
          canToggle={canToggle}
          onEdit={() => setDialogOpen(true)}
        />
      )}
      {hasReply && canToggle && (
        <ReplyDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          rejectionMessage={rejectionMessage}
          blockNewUsers={blockNewUsers}
          quietCount={quietCount}
          blockedCount={blockedCount}
          busy={setRejection.isPending}
          onSave={(m) => setRejection.mutate(m)}
        />
      )}
    </section>
  );
}

function ReplyBar({
  rejectionMessage,
  quietCount,
  blockedCount,
  canToggle,
  onEdit,
}: {
  rejectionMessage: string | null;
  quietCount: number;
  blockedCount: number;
  canToggle: boolean;
  onEdit: () => void;
}) {
  const coverage = coverageText(quietCount, blockedCount);
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-lg border border-border p-4",
        canToggle && "cursor-pointer hover:bg-muted/50",
      )}
      onClick={canToggle ? onEdit : undefined}
    >
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <MessageCircleOff className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="flex min-w-0 flex-1 basis-56 flex-col gap-0.5">
          <span className="text-sm font-medium">Not-available reply</span>
          <span className="text-sm text-muted-foreground text-pretty">
            Sent instead of an answer in quiet channels and to blocked people
          </span>
          {coverage && <span className="text-xs text-muted-foreground tabular-nums">Used for {coverage}</span>}
        </div>
        {canToggle && (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            {EDIT_REPLY_LABEL}
          </Button>
        )}
      </div>
      {rejectionMessage ? (
        // Full text, in Boo's own chat-bubble voice — the reply people most
        // want to check shouldn't hide behind a hover tooltip.
        <div className="flex items-start gap-2 pl-7">
          <Avatar className="size-6 shrink-0">
            <AvatarFallback className="text-[9px]">Boo</AvatarFallback>
          </Avatar>
          <div className="flex-1 rounded-xl bg-muted px-3 py-2 text-sm">{rejectionMessage}</div>
        </div>
      ) : (
        <p className="pl-7 text-sm text-muted-foreground">
          Boo sends a short, polite default until you write your own.
        </p>
      )}
    </div>
  );
}

function ReplyCase({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <li className="flex items-center gap-2.5">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground ring-1 ring-border">
        <Icon className="size-3.5" />
      </span>
      <span className="text-pretty">{children}</span>
    </li>
  );
}

// One room, many doors: opened from the bar and from every floating notice.
function ReplyDialog({
  open,
  onOpenChange,
  rejectionMessage,
  blockNewUsers,
  quietCount,
  blockedCount,
  busy,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rejectionMessage: string | null;
  blockNewUsers: boolean;
  quietCount: number;
  blockedCount: number;
  busy: boolean;
  onSave: (message: string | null) => void;
}) {
  // null = untouched, mirror the server value; a string = local edit in
  // progress. Kept through refetch so the field doesn't flash stale text.
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? rejectionMessage ?? "";
  const dirty = draft !== null && draft.trim() !== (rejectionMessage ?? "");
  const coverage = coverageText(quietCount, blockedCount);
  const fieldId = useId();
  const close = () => {
    setDraft(null);
    onOpenChange(false);
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setDraft(null);
        onOpenChange(next);
      }}
    >
      <DialogContent className="gap-5 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Not-available reply</DialogTitle>
          <DialogDescription className="text-pretty">
            Sent instead of an answer, at most once a minute per person.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg bg-muted/60 p-3">
          <p className="mb-2 text-xs font-medium text-muted-foreground">Boo sends it when</p>
          <ul className="flex flex-col gap-2 text-sm">
            <ReplyCase icon={AtSign}>Someone @-mentions Boo in a channel where it stays quiet</ReplyCase>
            <ReplyCase icon={Ban}>Someone you&apos;ve blocked writes to Boo</ReplyCase>
            {blockNewUsers && (
              <ReplyCase icon={UserPlus}>Someone new writes to Boo while they&apos;re still Blocked</ReplyCase>
            )}
          </ul>
          {coverage && (
            <p className="mt-3 border-t border-border/70 pt-2.5 text-xs text-muted-foreground tabular-nums">
              Right now that covers {coverage}.
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor={fieldId}>Reply</Label>
          <Textarea
            id={fieldId}
            rows={3}
            value={value}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. Sorry — I'm not on duty here. Ask in #help instead."
          />
          <p className="text-xs text-muted-foreground">
            {value.trim()
              ? "Sent exactly as written."
              : "Leave empty and Boo sends its own short, polite default."}
          </p>
        </div>
        <DialogFooter className="sm:items-center">
          {value.trim().length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground sm:mr-auto"
              disabled={busy}
              onClick={() => {
                setDraft("");
                onSave(null);
              }}
            >
              Use the default
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={close}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={busy || !dirty}
            onClick={() => {
              onSave(value.trim() || null);
              close();
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

